import {
  PunchHistoryChange,
  PunchHistoryEvent,
  PunchHistoryItem,
  PunchHistoryTimes,
} from '../../interfaces/TimesheetEditInterface';
import { changeWhoLabel, fmtInstant, fmtLogValue } from './changeLogFormat';

/**
 * TAS-365 — pure helpers for the per-punch history modal on the v2 review
 * console (EmployeeTimesheetReviewConsole). Data: GET /admin/timesheetEdit/
 * punchHistory. Times render in the PUNCH's own wall clock (its stored offset),
 * the same way the console's punch table renders them, so "7:02a" in the table
 * and "7:02a" in the popup always agree. When no offset is known the tenant
 * clock is used (fmtLogValue).
 */

/** Map key for a punch — the two tables' IDs overlap (both seed at 1001). */
export function punchHistoryKey(entityType: string, entityID: number): string {
  return `${entityType}:${entityID}`;
}

export function indexPunchHistory(items: PunchHistoryItem[] | null | undefined): Map<string, PunchHistoryItem> {
  const map = new Map<string, PunchHistoryItem>();
  (items ?? []).forEach((item) => map.set(punchHistoryKey(item.entityType, item.entityID), item));
  return map;
}

/** "-07:00" → -420; null when unparseable. */
export function offsetMinutes(offset: string | null | undefined): number | null {
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec((offset ?? '').trim());
  if (!m) return null;
  const mins = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
  return m[1] === '-' ? -mins : mins;
}

/** The wall clock an instant reads in an offset, as a UTC-fielded Date. */
function wall(iso: string, offset: string | null | undefined): Date | null {
  const t = Date.parse(iso);
  const off = offsetMinutes(offset);
  if (Number.isNaN(t) || off === null) return null;
  return new Date(t + off * 60_000);
}

/** 'YYYY-MM-DD' of an instant in an offset; null when either is missing. */
export function localDate(iso: string | null | undefined, offset: string | null | undefined): string | null {
  if (!iso) return null;
  const w = wall(iso, offset);
  return w ? w.toISOString().slice(0, 10) : null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A punch time in its own wall clock: "7:02a". When `dayISO` (the punch's day,
 * 'YYYY-MM-DD') is given and the time falls on another day, the date is added:
 * "Oct 6 · 1:15a". No offset → the tenant clock; no value → "—".
 */
export function fmtPunchClock(iso: string | null | undefined, offset: string | null | undefined, dayISO?: string | null): string {
  if (!iso) return '—';
  const w = wall(iso, offset);
  if (!w) return fmtInstant(iso, !dayISO);
  const h = w.getUTCHours();
  const clock = `${h % 12 || 12}:${String(w.getUTCMinutes()).padStart(2, '0')}${h >= 12 ? 'p' : 'a'}`;
  const day = w.toISOString().slice(0, 10);
  if (dayISO && day !== dayISO) return `${MONTHS[w.getUTCMonth()]} ${w.getUTCDate()} · ${clock}`;
  return clock;
}

/** The punch's day: its current IN, else its original IN, in its own offset. */
export function punchDay(item: PunchHistoryItem): string | null {
  return localDate(item.current?.in, item.current?.inOffset) ?? localDate(item.original.in, item.original.inOffset);
}

const FIELD_LABELS: Record<string, string> = {
  InDateTime: 'In',
  OutDateTime: 'Out',
  InDateTimeOffset: 'In offset',
  OutDateTimeOffset: 'Out offset',
  Type: 'Type',
  PunchTypeIn: 'In punch',
  PunchTypeOut: 'Out punch',
  ProjectID: 'Project',
  ProjectPhaseID: 'Phase',
  AccountCodeID: 'Account',
  ServiceOrderText: 'SO',
  ServiceOrderID: 'SO ID',
  TempServiceOrderDescription: 'Pending SO',
  DriveTimeEntryID: 'Drive time',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

const PUNCH_TYPE_TEXT: Record<string, string> = { 1: 'Button', 2: 'Manual add', 3: 'Manual edit' };
const ENTRY_TYPE_TEXT: Record<string, string> = { 1: 'REG', 2: 'MEAL' };

const TIME_FIELDS = new Set(['InDateTime', 'OutDateTime']);
const OFFSET_OF: Record<string, string> = { InDateTimeOffset: 'InDateTime', OutDateTimeOffset: 'OutDateTime' };

/** One side of a change, display-ready. */
export function fmtChangeValue(field: string, value: string | null, offset: string | null | undefined, dayISO?: string | null): string {
  if (value == null || value === '') return '—';
  if (TIME_FIELDS.has(field)) return offset ? fmtPunchClock(value, offset, dayISO) : fmtLogValue(field, value);
  if (field === 'PunchTypeIn' || field === 'PunchTypeOut') return PUNCH_TYPE_TEXT[value] ?? value;
  if (field === 'Type') return ENTRY_TYPE_TEXT[value] ?? value;
  return fmtLogValue(field, value);
}

/**
 * The changes a reader needs: an offset that moved in the same save as its own
 * time is already shown through that time (its wall clock), so it is dropped.
 */
export function visibleChanges(event: PunchHistoryEvent): PunchHistoryChange[] {
  if (event.action !== 'Edited') return event.changes;
  const moved = new Set(event.changes.map((c) => c.field).filter((f) => TIME_FIELDS.has(f)));
  return event.changes.filter((c) => !(OFFSET_OF[c.field] && moved.has(OFFSET_OF[c.field])));
}

/** Who made a change — the HistoryPanel's rules (KIOSK → "Name · kiosk", WEB → "Name · web"). */
export function punchEventWho(event: PunchHistoryEvent, employeeName: string): string {
  const selfService = event.source === 'EMPLOYEE' || event.source === 'KIOSK' || event.source === 'WEB';
  return changeWhoLabel(
    {
      source: event.source,
      adminEmployeeID: event.adminEmployeeID,
      adminEmployeeName: selfService ? null : event.actorName,
    },
    employeeName || (selfService ? event.actorName ?? '' : '')
  );
}

/** True when the event came from the old Audit text rather than a live log write. */
export function isImportedEvent(item: PunchHistoryItem, event: PunchHistoryEvent): boolean {
  return event.backfilled || item.origin === 'auditFallback';
}

/**
 * Whether a punch row offers its history: only rows the console badges Edited /
 * Manual, and the badge itself is the click target (TAS-365).
 */
export function offersPunchHistory(
  item: PunchHistoryItem | undefined,
  row: { isEdited: boolean; isManual: boolean; isOvernight?: boolean }
): boolean {
  // Only punches the console already badges Edited / Manual get the history affordance —
  // a plain button punch that was merely clocked out stays clean, even though its log
  // carries an Update for the clock-out. The Overnight badge is the exception: the
  // SYSTEM split that cuts an on-call shift at 12:00 AM keeps PunchType 1 (button), so
  // without it the split's own history would have no entry point.
  if (!item || item.events.length === 0) return false;
  return row.isEdited || row.isManual || !!row.isOvernight;
}

/**
 * Tooltip for the console's "Overnight" badge on one half of an overnight on-call shift
 * (2020CA/NV): the API flags a punch that ends at 12:00 AM and carries on the next day
 * (`overnightOut`) and one that starts at 12:00 AM carrying on from the previous day
 * (`overnightIn`). Null when the punch is neither — no badge.
 */
export function overnightBadgeLabel(entry: { overnightOut?: boolean; overnightIn?: boolean }): string | null {
  if (entry.overnightIn && entry.overnightOut) {
    return 'Overnight shift — continued from the previous day and continues on the next day';
  }
  if (entry.overnightOut) return 'Overnight shift — continues on the next day';
  if (entry.overnightIn) return 'Overnight shift — continued from the previous day';
  return null;
}

/**
 * The punch history modal's title for a console punch row: what the row's Job / Code
 * cell shows. A non-job punch has no job name (bare group, `project.name` null), so
 * the cell's sub-line — the account, e.g. "Non-Job Time" — names it instead. Never
 * renders "null" / "undefined".
 */
export function punchHistoryTitle(row: { jobName?: string | null; jobSub?: string | null }): string {
  const label = [row.jobName, row.jobSub]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '' && v !== 'null' && v !== 'undefined');
  return `${label ?? 'Punch'} — punch history`;
}

/** The note the punch history modal shows for one half of an overnight shift. */
export function overnightHistoryNote(entry: { overnightOut?: boolean; overnightIn?: boolean }): string | null {
  const label = overnightBadgeLabel(entry);
  return label ? `${label} (12:00 AM split)` : null;
}

/**
 * The day-level Overnight label (week matrix Alerts row, day card flags): the day has a
 * punch ending at 12:00 AM that continues next day and/or one starting at 12:00 AM that
 * continues from the previous day. Informational — never counted as an alert.
 */
export function overnightDayLabel(entries: ReadonlyArray<{ overnightOut?: boolean; overnightIn?: boolean }>): string | null {
  return overnightBadgeLabel({
    overnightOut: entries.some((e) => !!e.overnightOut),
    overnightIn: entries.some((e) => !!e.overnightIn),
  });
}

/** Deleted punches whose day is `dayISO`, earliest first. */
export function removedPunchesForDay(items: PunchHistoryItem[] | null | undefined, dayISO: string): PunchHistoryItem[] {
  return (items ?? [])
    .filter((i) => i.deleted && punchDay(i) === dayISO)
    .sort((a, b) => Date.parse(a.original.in ?? '') - Date.parse(b.original.in ?? ''));
}

/** True when the original differs from what the punch reads now. */
export function timesChanged(original: PunchHistoryTimes, current: PunchHistoryTimes | undefined): { in: boolean; out: boolean } {
  if (!current) return { in: false, out: false };
  const same = (a: string | null, b: string | null) =>
    (a == null && b == null) || (a != null && b != null && Date.parse(a) === Date.parse(b));
  return {
    in: original.in != null && !same(original.in, current.in),
    out: original.out != null && !same(original.out, current.out),
  };
}

export function entityKindLabel(entityType: string): string {
  return entityType === 'TimeEntry' ? 'Piece punch' : entityType === 'TimeEntryHourly' ? 'Hourly punch' : entityType;
}
